import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * FormApiService (Angular 18) — form definitions, responses, locations, and
 * custom fields.
 *
 * Replaces apps/b2b-ui/common/services/api/formApi/formApi.service.js.
 *
 * Five sub-namespaces:
 *   apiService.form.responses.{list,detail,create,update,delete}
 *   apiService.form.states.list                                    states by country
 *   apiService.form.countries.list
 *   apiService.form.avs.countries                                  AVS-supported countries
 *   apiService.form.customFields.{list,create,update,remove}
 *
 * Legacy quirks preserved:
 *   - `update(props, publish?)` appends `?publish=true` when truthy.
 *   - `create()` whitelists a fixed payload shape; extra fields are dropped.
 *     Notably `sourceFormId` is forced to equal `props.id` (legacy quirk —
 *     callers pass the form id and the API expects it as `sourceFormId`).
 */
const FORMS_BASE_V2 = '/v2/api/forms';
const FORM_RESPONSES_BASE_V2 = '/v2/api/forms/responses';
const STATES_BASE_V2 = '/v2/api/locations/states';
const COUNTRIES_BASE_V2 = '/v2/api/locations/countries';
const AVS_COUNTRIES_BASE_V2 = '/v2/api/locations/avs/countries';
const FORM_CUSTOM_FIELDS_BASE_V2 = '/v2/api/forms/customfields';

const FORM_LIST_FIELDS = ['published', ...PAGINATION_SEARCH_FIELDS, 'formCategoryType'] as const;

interface FormPayload {
  id?: string | number;
  name: string;
  description?: string;
  type?: string;
  source?: string;
  sourceFormUrl?: string;
  sourceFormEditUrl?: string;
  sourceFormThumbnail?: string;
  sourceFormDefinition?: unknown;
  state?: string;
  lastUpdatedDate?: unknown;
  lastPublishedDate?: unknown;
  oncePerCart?: boolean;
  formCategoryType?: string;
  formDisplaySettings?: unknown;
}

interface FormResponsePayload {
  id?: number | string;
  formId: number | string;
  name?: string;
  type?: string;
  source?: string;
  number?: number | string;
  sourceFormId?: number | string;
  sourceFormResponseId?: number | string;
  sourceFormUploads?: unknown;
  customFormFields?: unknown;
  customFields?: unknown;
  responseData?: unknown;
  submissionDate?: unknown;
}

@Injectable({ providedIn: 'root' })
export class FormApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(FORMS_BASE_V2, {
        params: buildParams(props, FORM_LIST_FIELDS),
      }),
    );
  }

  detail(props: { id: number | string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${FORMS_BASE_V2}/${props.id}`));
  }

  published(props: { id: number | string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${FORMS_BASE_V2}/${props.id}/contentfrompublished`));
  }

  create(props: FormPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(FORMS_BASE_V2, {
        name: props.name,
        description: props.description,
        type: props.type,
        source: props.source,
        sourceFormId: props.id,
        sourceFormUrl: props.sourceFormUrl,
        sourceFormEditUrl: props.sourceFormEditUrl,
        sourceFormThumbnail: props.sourceFormThumbnail,
        sourceFormDefinition: props.sourceFormDefinition,
        state: props.state,
        lastUpdatedDate: props.lastUpdatedDate,
        lastPublishedDate: props.lastPublishedDate,
        oncePerCart: props.oncePerCart,
        formCategoryType: props.formCategoryType,
        formDisplaySettings: props.formDisplaySettings,
      }),
    );
  }

  update(props: FormPayload, publish?: boolean): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(
        `${FORMS_BASE_V2}/${props.id}`,
        {
          id: props.id,
          name: props.name,
          description: props.description,
          type: props.type,
          source: props.source,
          sourceFormId: props.id,
          sourceFormUrl: props.sourceFormUrl,
          sourceFormEditUrl: props.sourceFormEditUrl,
          sourceFormThumbnail: props.sourceFormThumbnail,
          sourceFormDefinition: props.sourceFormDefinition,
          state: props.state,
          lastUpdatedDate: props.lastUpdatedDate,
          lastPublishedDate: props.lastPublishedDate,
          oncePerCart: props.oncePerCart,
          formCategoryType: props.formCategoryType,
          formDisplaySettings: props.formDisplaySettings,
        },
        {
          params: publish ? buildParams({ publish: 'true' }) : undefined,
        },
      ),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${FORMS_BASE_V2}/${id}`));
  }

  readonly responses = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(FORM_RESPONSES_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${FORM_RESPONSES_BASE_V2}/${props.id}`)),

    create: (props: FormResponsePayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(FORM_RESPONSES_BASE_V2, {
          formId: props.formId,
          name: props.name,
          type: props.type,
          source: props.source,
          number: props.number,
          sourceFormId: props.sourceFormId,
          sourceFormResponseId: props.sourceFormResponseId,
          sourceFormUploads: props.sourceFormUploads,
          customFormFields: props.customFormFields,
          responseData: props.responseData,
          submissionDate: props.submissionDate,
        }),
      ),

    update: (props: FormResponsePayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${FORM_RESPONSES_BASE_V2}/${props.id}`, {
          id: props.id,
          formId: props.formId,
          number: props.number,
          name: props.name,
          type: props.type,
          source: props.source,
          customFields: props.customFields,
          responseData: props.responseData,
          submissionDate: props.submissionDate,
        }),
      ),

    delete: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${FORM_RESPONSES_BASE_V2}/${props.id}`)),
  };

  readonly states = {
    list: (props: { country: string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${STATES_BASE_V2}/${props.country}`)),
  };

  readonly countries = {
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(COUNTRIES_BASE_V2)),
  };

  readonly avs = {
    countries: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(AVS_COUNTRIES_BASE_V2)),
  };

  readonly customFields = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(FORM_CUSTOM_FIELDS_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    create: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(FORM_CUSTOM_FIELDS_BASE_V2, props)),

    update: (props: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(FORM_CUSTOM_FIELDS_BASE_V2, props)),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${FORM_CUSTOM_FIELDS_BASE_V2}/${id}`)),
  };
}
